import { IDetailsRowProps, MessageBarType } from '@fluentui/react';
import { useBoolean } from '@fluentui/react-hooks';
import {
  BaseDialog,
  defaultPalette,
  detailsHeaderStylesBasic,
  DetailsList,
  detailsListStylesCard,
  EmptyMessage,
  IColumnWithDataType,
  IDetailsListProps,
  Link,
  linkStylesGhost,
  ListRow,
  Loader,
  loaderStylesSpinnerXLarge,
  MessageBar,
  useClassNames,
  useToast,
} from '@h2oai/ui-kit';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { fsApi } from '../../featurestore/api';
import {
  CoreService,
  FeatureSetVersionDescription,
  ListFeatureSetsVersionRequest,
  ListFeatureSetVersionsResponse,
} from '../../featurestore/gen/ai/h2o/featurestore/api/v1/CoreService.pb';
import { getErrorMessageBarMessage } from '../../featurestore/utils';
import { RoutePaths } from '../../pages/Routes';
import { linkStylesIconOnly } from '../ScheduledTaskList/ScheduledTaskList.styles';
import { IVersionListClassNames, IVersionListStyles, versionListStylesDefault } from './VersionList.styles';

const getColumns = (
  classNames: IVersionListClassNames,
  onView: (item: FeatureSetVersionDescription) => void,
  onDelete?: (item: FeatureSetVersionDescription) => void
): IColumnWithDataType[] => {
  const columns: IColumnWithDataType[] = [
    {
      key: 'version',
      name: 'Version',
      fieldName: 'version',
      minWidth: 180,
      maxWidth: 180,
      data: {
        listCellProps: { iconProps: { iconName: 'FormLibraryMirrored' } },
      },
    },
    {
      key: 'versionChange',
      name: 'Version Change',
      fieldName: 'versionChange',
      minWidth: 200,
    },
    {
      key: 'createdDateTime',
      name: 'Created At',
      fieldName: 'createdDateTime',
      minWidth: 150,
    },
    {
      key: 'action',
      name: 'Action',
      minWidth: 170,
      onRender: (item: FeatureSetVersionDescription) => {
        return (
          <div className={classNames.actions}>
            <div className={classNames.actionButtons}>
              {onDelete && (
                <Link
                  title="Delete"
                  styles={[linkStylesGhost, linkStylesIconOnly]}
                  data-test="delete-version-button"
                  iconName="Delete"
                  onClick={() => onDelete(item)}
                ></Link>
              )}
              <Link styles={linkStylesGhost} onClick={() => onView(item)} data-test="view-version-button">
                View
              </Link>
            </div>
          </div>
        );
      },
    },
  ];
  return columns;
};

interface IVersionListProps {
  isOwner?: boolean;
  reload?: boolean;
  featureSetId?: string;
  onView: (item: FeatureSetVersionDescription) => void;
  onDelete: () => void;
}

export const VersionList = ({ reload, featureSetId, onView, onDelete: onDeleteItem, isOwner }: IVersionListProps) => {
  const [data, setData] = useState<FeatureSetVersionDescription[]>([]);
  const [hideDialog, { toggle: toggleHideDialog }] = useBoolean(true);
  const [loadingMessage, setLoadingMessage] = useState<string>('');
  const history = useHistory();
  const { addToast } = useToast();
  const refMeta = useRef<{
    mounted?: boolean;
    selectedItem?: FeatureSetVersionDescription;
  }>({});
  const load = useCallback(async () => {
    setLoadingMessage(`Loading Feature Set Versions...`);
    try {
      const req: ListFeatureSetsVersionRequest = {
        featureSetId,
      };
      const { versions = [] }: ListFeatureSetVersionsResponse = await fsApi(CoreService.ListFeatureSetVersions, req);
      if (!refMeta.current.mounted) return;
      setData(versions);
    } catch (err: any) {
      addToast(getErrorMessageBarMessage(err, `An error has occurred while loading Feature Set Versions: `));
      if (err.reason && err.reason.toLowerCase().includes('does not exist')) {
        history.push(RoutePaths.FEATURESTORE_FEATURE_SETS);
      }
    } finally {
      if (!refMeta.current.mounted) return;
      setLoadingMessage('');
    }
  }, [addToast]);
  const deleteItem = useCallback(async (item?: FeatureSetVersionDescription) => {
    if (!item) return;
    setLoadingMessage(`Deleting the version ${item.version}...`);
    try {
      if (item.version) {
        const featureSetMajorVersion = parseInt(item.version);
        await fsApi(CoreService.DeleteFeatureSetVersion, {
          featureSetId,
          featureSetMajorVersion,
        });
        addToast({
          messageBarType: MessageBarType.success,
          message: `The major version (${featureSetMajorVersion}) has been deleted successfully!`,
        });
      }
    } catch (err) {
      addToast(getErrorMessageBarMessage(err, `An error has occurred while deleting feature set version: `));
    } finally {
      setLoadingMessage('');
      load();
      onDeleteItem();
    }
  }, []);
  const onDelete = useCallback((item: FeatureSetVersionDescription) => {
    refMeta.current.selectedItem = item;
    toggleHideDialog();
  }, []);
  useEffect(() => {
    refMeta.current.mounted = true;
    load();
    return () => {
      refMeta.current.mounted = false;
    };
  }, [load]);
  useEffect(() => {
    reload && load();
  }, [reload]);
  const classNames = useClassNames<IVersionListStyles, IVersionListClassNames>('VersionList', versionListStylesDefault);
  const detailsListProps: IDetailsListProps = {
    styles: detailsListStylesCard,
    headerStyles: detailsHeaderStylesBasic,
    isHeaderVisible: false,
    columns: getColumns(classNames, onView, isOwner ? onDelete : undefined),
    items: data,
    cellStyleProps: {
      cellExtraRightPadding: 0,
      cellLeftPadding: 0,
      cellRightPadding: 0,
    },
    onRenderRow: (props?: IDetailsRowProps) => {
      const { item, columns = [] } = props || {};
      return <ListRow data={item} columns={columns} />;
    },
  };
  return (
    <div className={classNames.root} data-test="feature-set-version-list">
      {loadingMessage ? (
        <Loader styles={loaderStylesSpinnerXLarge} label={loadingMessage} />
      ) : (
        <div className={classNames.cardView} data-is-scrollable="true" data-test="feature-set-version-list-card-view">
          {data.length === 0 ? (
            <EmptyMessage message="No Feature Set Versions Available" />
          ) : (
            <DetailsList {...detailsListProps} />
          )}
        </div>
      )}
      {isOwner && (
        <BaseDialog
          hidden={hideDialog}
          onDismiss={toggleHideDialog}
          title={`Delete The Major Version ${parseInt(refMeta.current.selectedItem?.version || '')}`}
          content={
            <MessageBar messageBarType={MessageBarType.error}>
              Are you sure you want to delete the major version{' '}
              <strong>{parseInt(refMeta.current.selectedItem?.version || '')}</strong> along with all its associated
              minor versions?
              <br />
              <strong>The user will lost all its associated minor versions</strong>
            </MessageBar>
          }
          footer={
            <div data-test="dialog-footer">
              <Link styles={[linkStylesGhost, { root: { marginRight: 8 } }]} onClick={toggleHideDialog}>
                Cancel
              </Link>
              <Link
                styles={[
                  linkStylesGhost,
                  {
                    root: {
                      borderColor: defaultPalette.red500,
                      backgroundColor: defaultPalette.red500,
                      color: defaultPalette.white,
                    },
                  },
                ]}
                onClick={() => {
                  toggleHideDialog();
                  deleteItem(refMeta.current.selectedItem);
                }}
              >
                Delete
              </Link>
            </div>
          }
        ></BaseDialog>
      )}
    </div>
  );
};
